import {
  CtaProcessingOption,
  RestModalAction,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import {
  navigateInThisWindow,
  type EventSyncObject,
  type FunnelNavigate,
} from '@bbkFunnel/events';

export const isCheckoutAction = (action: RestModalForwards): boolean =>
  (action.action === RestModalAction.link ||
    action.action === RestModalAction.accept_offer ||
    action.action === RestModalAction.accept_offer_with_confirmation) &&
  !!action.cta_details_list?.some(
    (cta) => cta.processing_option === CtaProcessingOption.chargebee_checkout
  );

export const createCancelCheckout = async ({
  id,
  cta_ids,
}: Pick<EventSyncObject, 'id'> & { cta_ids?: string[] }): Promise<string> => {
  const sessionKey = /^\/session\/([^/]+)\/?$/.exec(
    window.location.pathname
  )?.[1];
  if (!sessionKey || !window.bbkAuthenticityToken) {
    throw new Error('This session is unavailable. Please start again.');
  }
  // A plan change with a coupon is two CTAs on this button. Name every id so
  // checkout cannot charge the plan change without that coupon.
  if (!id || !cta_ids?.length || cta_ids.some((ctaId) => !ctaId)) {
    throw new Error('This offer is not available for checkout.');
  }

  // Do not retry a POST: a lost response can still have created a checkout.
  const response = await fetch(
    `/api/v1/session/${sessionKey}/checkout-session`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'BBK-Authenticity-Token': window.bbkAuthenticityToken,
      },
      body: JSON.stringify({ offer_id: id, cta_ids }),
    }
  );
  const body = (await response.json().catch(() => null)) as {
    message?: unknown;
    url?: unknown;
  } | null;
  if (!response.ok) {
    throw new Error(
      typeof body?.message === 'string'
        ? body.message
        : 'Could not open checkout. Please try again.'
    );
  }
  if (
    typeof body?.url !== 'string' ||
    new URL(body.url).protocol !== 'https:'
  ) {
    throw new Error('Checkout returned an invalid URL. Please try again.');
  }
  return body.url;
};

/** True while this page is navigating; false when checkout opened in a new tab. */
export const openCancelCheckout = (
  url: string,
  navigate: FunnelNavigate = navigateInThisWindow
): boolean => {
  try {
    navigate(url);
    return true;
  } catch {
    // Console-only browser diagnostics do not throw and cannot be caught here.
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      throw new Error('Allow pop-ups, then try opening checkout again.');
    }
    tab.opener = null;
    tab.location.replace(url);
    return false;
  }
};
